<template>
    <div class="project-overview">
        <design-2021-apprentice-multistep-form-wizard-form-header-component
            :title="$t('group.create.overview')"
            :description="description">
        </design-2021-apprentice-multistep-form-wizard-form-header-component>

        <div class="project-overview-content">
            <div class="overview-group project-name-group">
                <h4 class="title">{{ steps[0].title }}</h4>
                {{ formData.project_name }}
            </div>

            <div class="overview-group settings-group">
                <h4 class="title">{{ steps[1].title }}</h4>
                <div class="course-setting">
                    <div class="course">
                        <span class="label">{{ $t('group.create.course') }}:</span>
                        <span class="text">{{ formData.settings.course.name }}</span>
                    </div>
                    <div class="custom-banner-setting">
                        <span class="label">{{ $t('group.create.custom_banner') }}:</span>
                        <div
                            class="banner-preview"
                            v-if="formData.settings.banner !== '' && formData.settings.banner !== null">
                            <img
                                :src="getFileUrl()"
                                alt="banner" />
                        </div>
                        <span
                            class="text"
                            v-else>
                            {{ $t('group.create.no_custom_banner') }}
                        </span>
                    </div>
                </div>

                <div class="seasons-setting">
                    <div class="season">
                        <span class="label">{{ $t('group.create.school_year') }}:</span>
                        <span class="text">{{ formData.settings.season.season }}</span>
                    </div>
                    <div class="start-date">
                        <span class="label">{{ $t('group.create.start_date') }}:</span>
                        <span class="text">{{ formData.settings.start_date | date_named_month }}</span>
                    </div>
                    <div
                        class="start-time"
                        v-if="formData.settings.start_time !== '00:00:00'">
                        <span class="label">{{ $t('group.create.project_visible_from') }}:</span>
                        <span
                            class="text"
                            v-if="formData.settings.start_time">
                            {{ formData.settings.start_time.slice(0, formData.settings.start_time.lastIndexOf(':')) }}
                            {{ $t('group.create.hour') }}</span
                        >
                    </div>
                    <div class="end-date">
                        <span class="label">{{ $t('group.create.end_date') }}:</span>
                        <span class="text">{{ formData.settings.end_date | date_named_month }}</span>
                    </div>
                </div>

                <div
                    class="editor-type"
                    v-if="formData.settings.type !== 4">
                    <span class="label">{{ $t('common.editor_type') }}</span>
                    <span class="text">{{ formData.settings.editor_type.text }}</span>
                </div>
            </div>

            <div
                class="overview-group project-duplicate-from-group"
                v-if="formData.duplicate && formData.duplicate.source_id !== null && formData.settings.type !== 7">
                <h4 class="title">{{ steps[4].title }}</h4>
                "{{ $t('group.create.group_copy_off') + ' ' + formData.duplicate.source_name }}"
            </div>
            <div
                class="overview-group project-duplicate-form-group"
                v-if="formData.settings.type === 7">
                <h4 class="title">{{ steps[4].title }}</h4>
                "{{ $t('group.create.use_lesson_module') + ' ' + formData.duplicate.source_name }}"
            </div>

            <div class="overview-group coordinates-group">
                <h4 class="title">{{ steps[2].title }}</h4>
                <ul
                    class="teachers-list"
                    v-if="formData.coordinators.length > 0">
                    <li
                        class="teacher"
                        v-for="coordinator in formData.coordinators"
                        :key="coordinator.id">
                        <i class="icon fa-fw fad fa-user"></i>
                        {{ coordinator.full_name }}
                    </li>
                </ul>

                <div
                    class="no-teachers-added"
                    v-else>
                    {{ $t('group.create.no_administrators_added') }}
                </div>
            </div>

            <div
                class="overview-group sub-project-group"
                v-if="
                    formData.subprojects &&
                    (formData.subprojects.groups.length > 0 || formData.subprojects.main_groups.length > 0)
                ">
                <h4 class="title">{{ steps[5].title }}</h4>
                <div class="info">{{ $t('group.create.subprojects_added_with_las_groups') }}</div>

                <div
                    class="groups"
                    v-if="formData.subprojects.groups.length > 0">
                    <p class="title">{{ $t('group.create.groups') }}</p>
                    <b-list-group>
                        <b-list-group-item
                            v-for="group of formData.subprojects.groups"
                            :key="group.id">
                            {{ group.name }}
                        </b-list-group-item>
                    </b-list-group>
                </div>

                <div
                    class="main-groups"
                    v-if="formData.subprojects.main_groups.length > 0">
                    <p class="title">{{ $t('group.create.main_groups') }}</p>
                    <b-list-group>
                        <b-list-group-item
                            v-for="main_group of formData.subprojects.main_groups"
                            :key="main_group.id">
                            {{ main_group.name }}
                        </b-list-group-item>
                    </b-list-group>
                </div>
            </div>

            <div class="overview-group options-group">
                <h4 class="title">{{ steps[3].title }}</h4>
                <ul
                    class="options-list"
                    v-if="hasOptionsEnabled()">
                    <li
                        class="option"
                        v-for="option in formData.options"
                        v-if="option.enabled">
                        <i :class="getIconClass(option)"></i>
                        {{ option.label }}

                        <b-list-group
                            class="linked-rating-items"
                            v-if="option.key === 'rating' && hasLinkedRatings()">
                            <!--                            <div class="rating-header">{{getRatingsables().length > 1 ? $t('group.create.linked_ratings') : $t('group.create.linked_rating')}}</div>-->
                            <b-list-group-item
                                class="linked-rating"
                                v-for="ratingable in getRatingsables()"
                                v-if="ratingable.enabled"
                                :key="ratingable.rating_id">
                                <i class="fad fa-file-certificate icon"></i
                                >{{ ratingable.custom_title !== '' ? ratingable.custom_title : ratingable.title }}
                            </b-list-group-item>
                        </b-list-group>

                        <div
                            class="linked-roadmap-items"
                            v-if="option.linked_roadmap">
                            <i class="fad fa-file-invoice icon"></i>{{ option.linked_roadmap.title }}
                        </div>
                    </li>
                </ul>

                <div
                    class="no-teachers-added"
                    v-else>
                    {{ $t('group.create.no_options_selected') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment/moment';

export default {
    name: 'design-2021-apprentice-multistep-wizard-project-overview-step-component',
    computed: {
        moment() {
            return moment;
        },
    },
    props: {
        steps: {
            type: Array,
            required: true,
        },
        formData: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            description: this.$t('group.create.check_explanation'),
        };
    },

    methods: {
        getFileUrl() {
            if (this.formData.settings.banner === '' || this.formData.settings.banner === null) {
                return;
            }

            const file = this.formData.settings.banner;
            return URL.createObjectURL(file);
        },

        getIconClass(optionType) {
            switch (optionType.key) {
                case 'magazine':
                    return 'icon fad fa-fw fa-book-open';

                case 'agenda':
                    return 'icon fa-fw fad fa-calendar-alt';

                case 'messages':
                    return 'icon fa-fw fad fa-envelope';

                case 'files':
                    return 'icon fa-fw fad fa-file-alt';

                case 'sourcelist':
                    return 'icon fa-fw fad fa-search';

                case 'logbook':
                    return 'icon fa-fw fad fa-clock';

                case 'rating':
                    return 'icon fa-fw fad fa-award';

                case 'powerpoint':
                    return 'icon fa-fw fad fa-presentation';

                case 'roadmap':
                    return 'icon fa-fw fad fa-dice';

                case 'plagiarism':
                    return 'icon fa-fw fad fa-check';

                case 'actionplan':
                    return 'icon fa-fw fad fa-file-contract';

                case 'worldmap':
                    return 'icon fa-fw fad fa-globe-americas';
            }
        },

        hasOptionsEnabled() {
            let tally = 0;

            for (const option of this.formData.options) {
                option.enabled === true && tally++;
            }

            return tally > 0;
        },

        hasLinkedRatings() {
            let found = false;

            for (const option of this.formData.options) {
                if (option.key === 'ratingsables') {
                    for (const rating of option.ratingsables) {
                        if (rating.enabled) {
                            found = true;
                            break;
                        }
                    }
                }
            }

            return found;
        },

        getRatingsables() {
            const index = this.formData.options.map((option) => option.key).indexOf('ratingsables');
            if (index === -1) {
                return;
            }

            return this.formData.options[index].ratingsables.filter((ratingable) => ratingable.enabled);
        },
    },
};
</script>

<style scoped lang="scss">
ul {
    list-style: none;
    padding: 0;
}

.overview-group {
    background-color: $secondary-background-color;
    border-radius: $border-radius-content;
    padding: map-get($grid-gutter-spacing, 'fifteen');
    font-size: $base-font-size;
    line-height: 1.5;

    > div > div {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }

    .title {
        border-bottom: 2px solid $border-color;
        padding-bottom: map-get($grid-gutter-spacing, 'eight');
    }

    .label {
        margin-right: map-get($grid-gutter-spacing, 'eihgt');
    }

    &:not(:last-child) {
        margin-bottom: $grid-gutter-width;
    }
}

.banner-preview {
    width: 100%;
    height: 100px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
}

.teacher,
.option {
    .icon {
        color: var(--primary);
        font-size: $base-font-size;
        margin-right: map-get($grid-gutter-spacing, 'ten');
    }
}

.linked-rating-items,
.linked-roadmap-items {
    padding-left: 35px;
}

.linked-rating {
    border: none;
    background-color: transparent;
    padding: 4px 0;
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .project-overview-content {
        display: flex;
        column-gap: $grid-gutter-width;
        flex-wrap: wrap;
        row-gap: $grid-gutter-width;
    }

    .overview-group {
        flex: 0 1 calc(50% - 15px);
        margin-bottom: 0;
        width: 100%;
        max-width: calc(50% - 15px);

        &:not(:last-child) {
            margin-bottom: 0;
        }
    }

    .project-name-group {
        flex: 1 0 100%;
        max-width: 100%;
    }

    .sub-project-group {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: space-between;
        column-gap: 15px;

        > .title {
            flex: 1 0 100%;
        }

        .info {
            flex: 1 0 100%;
            color: $text-muted-color;
            font-size: $default-font-size;
            font-weight: $font-weight-medium;
            line-height: 1.5;
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        .groups,
        .main-groups {
            flex: 0 0 calc(50% - 15px);

            .title {
                font-size: 1.125rem;
                font-weight: $font-weight-medium;
                border: none;
                margin-bottom: 0;
            }

            .list-group {
                margin: 0;
            }

            .list-group-item {
                border: none;
                background: transparent;
                padding: 4px 10px;
                font-size: $default-font-size;
            }
        }
    }
}
</style>
